import {
  SceneContextMenuEvent,
  useSceneEvents,
} from "@/components/common/scene-events-context";
import { useGoToAnnotation } from "@/hooks/use-go-to-annotation";
import { selectCurrentScene } from "@/modes/mode-selectors";
import { selectCurrentAreaData } from "@/modes/select-current-area-data";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import { selectModeName } from "@/store/mode-selectors";
import { selectActiveElement } from "@/store/selections-selectors";
import { setActiveElement } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  useNonExhaustiveEffect,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { assert } from "@faro-lotv/foundation";
import { IElement, isIElementMarkup } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { useEffect } from "react";
import { Vector3 } from "three";

/**
 * Type definitions for the IFrame API events and actions. These can also be used in the parent website
 * and currently have to be copied to the github example repo, when updated.
 */

/** Included in all messages, to specify they are part of the IFrame API */
type IFrameApiBase = {
  /** Base attribute describing the message as part of the IFrame API, to distinguish it from other postMessages. */
  type: "iframe-api";
};

/** Event emitted when the scene visible to the user in the main window changes */
type ChangedSceneEvent = IFrameApiBase & {
  /** Event type unique to ChangedSceneEvent */
  event: "changedScene";
  /** Full object for the IElement, which is currently shown */
  element: IElement;
};

/** Event emitted when the user right-clicks in the 3D view (when the dialog showing the current coordinates is also displayed) */
type RightClickedCoordinate = IFrameApiBase & {
  /** Event type unique to RightClickedCoordinate */
  event: "rightClickedCoordinate";
  /** Coordinate, where the user right clicked in a 3D view */
  coordinate: { x: number; y: number; z: number };
};

/** Event emitted, when the view mode changes, for example walk or sheet */
type ChangedViewModeEvent = IFrameApiBase & {
  /** Event type unique to ChangedViewModeEvent */
  event: "changedViewMode";
  /** Current view mode selected by the user / switched to automatically */
  mode: string;
};

/** All possible events emitted by the IFrame API */
type SphereXGIFrameAPIEvent =
  | ChangedSceneEvent
  | RightClickedCoordinate
  | ChangedViewModeEvent;

/** Show the provided IElement in the main view. Has to be a viewable IElement. */
type LookAtIdAction = IFrameApiBase & {
  /** Action type unique to LookAtIdAction */
  action: "lookAtId";
  /** ID of the IElement to show in the viewer. Has to be an element, which is able to be shown. */
  id: string;
};

/** Zoom to the provided coordinates in the current view. */
type LookAtPositionAction = IFrameApiBase & {
  /** Action type unique to LookAtPositionAction */
  action: "lookAtPosition";
  /** Position to zoom to in the current view. */
  position: { x: number; y: number; z: number };
};

/** Jump to the provided annotation and show it in the center of the screen. */
type LookAtAnnotationIdAction = IFrameApiBase & {
  /** Action type unique to LookAtAnnotationIdAction */
  action: "lookAtAnnotationId";
  /** Id of the annotation to show to the user as a popup. */
  id: string;
};

// Actions are sent from the host website to the SphereXG Viewer in the iframe, to trigger a change.
type SphereXGIFrameAPIAction =
  | LookAtIdAction
  | LookAtPositionAction
  | LookAtAnnotationIdAction;

/**
 * Component to register the IFrame API and send events to the parent window.
 *
 * @returns null
 */
export function IFrameApi(): null {
  const activeElement = useAppSelector(selectActiveElement);
  const currentAreaData = useAppSelector(selectCurrentAreaData(activeElement));
  const activeCadModel = useAppSelector(selectActiveCadModel);
  const scene = useAppSelector(
    selectCurrentScene(activeElement, activeCadModel, currentAreaData),
  );
  const viewMode = useAppSelector(selectModeName);
  const sceneEvents = useSceneEvents();

  // Register listener for events from the parent window
  useRegisterEventListeners();

  // Dispatch active element changed event
  useEffect(() => {
    if (scene?.activeElement) {
      sendEventToParent<ChangedSceneEvent>({
        event: "changedScene",
        element: scene.activeElement,
      });
    }
  }, [scene?.activeElement]);

  // Dispatch view mode changed event
  useEffect(() => {
    sendEventToParent<ChangedViewModeEvent>({
      event: "changedViewMode",
      mode: viewMode,
    });
  }, [viewMode]);

  // Dispatch right click event with coordinates
  useTypedEvent<SceneContextMenuEvent>(
    sceneEvents.openSceneContextMenuFrom3DView,
    (event: SceneContextMenuEvent) => {
      if (event.point !== undefined) {
        sendEventToParent<RightClickedCoordinate>({
          event: "rightClickedCoordinate",
          coordinate: {
            x: event.point[0],
            y: -event.point[2],
            z: event.point[1],
          },
        });
      }
    },
  );

  return null;
}

/**
 * Register listener, which receives events from the parent window containing the viewer in an IFrame.
 */
function useRegisterEventListeners(): void {
  const dispatch = useAppDispatch();
  const appStore = useAppStore();
  const goToAnnotation = useGoToAnnotation();
  const { lookAt } = useSceneEvents();

  useNonExhaustiveEffect(() => {
    function iframeApiListener(
      event: MessageEvent<Partial<SphereXGIFrameAPIAction>>,
    ): void {
      // Check if the message really is of type "iframe-api" and from the parent window because we can receive messages from other sources
      if (
        event.origin !== getParentOrigin() ||
        event.data.type !== "iframe-api"
      ) {
        return;
      }

      switch (event.data.action) {
        case "lookAtId": {
          // Set active element
          const activeElementId = event.data.id;
          const activeElement = selectIElement(activeElementId)(
            appStore.getState(),
          );
          assert(
            activeElement !== undefined,
            "The provided Id must be for a valid displayable IElement",
          );
          dispatch(setActiveElement(activeElementId));
          break;
        }
        case "lookAtPosition": {
          // Look at point in current scene
          const pos = event.data.position;
          assert(
            pos !== undefined &&
              typeof pos.x === "number" &&
              typeof pos.y === "number" &&
              typeof pos.z === "number",
            "Position must be a valid Vector3",
          );
          lookAt.emit(new Vector3(pos.x, pos.z, -pos.y));
          break;
        }
        case "lookAtAnnotationId": {
          // Look at annotation
          const annotationId = event.data.id;
          if (annotationId) {
            const annotation = selectIElement(annotationId)(
              appStore.getState(),
            );
            assert(
              annotation && isIElementMarkup(annotation),
              "Annotation not found or not a markup IElement",
            );
            goToAnnotation(annotation);
          }
          break;
        }
        default:
          throw new Error(
            `SphereXG IFrame API received message with unknown action: ${event.data.action}`,
          );
      }
    }
    window.addEventListener("message", iframeApiListener);

    return () => {
      window.removeEventListener("message", iframeApiListener);
    };
  }, []);
}

/**
 * Post a message containing the event data to the parent.
 *
 * @param msg event data to send to the parent
 */
function sendEventToParent<T extends SphereXGIFrameAPIEvent>(
  msg: Omit<T, "type">,
): void {
  const message = {
    type: "iframe-api",
    ...msg,
  };
  window.parent.postMessage(message, getParentOrigin());
}

function getParentOrigin(): string {
  // Used to verify messages originate from the parent window
  // Note: window.location.ancestorOrigins is not supported in firefox, so we use referrer instead
  return new URL(document.referrer).origin;
}
